/*
 * The drag-and-drop image slot, lifted out of `pages/Admin.tsx` unchanged.
 *
 * It moved because the v3 Landing screen needs it too, and a second uploader would be a second set
 * of rules about crop ratios, size limits and what happens to the old key. One component, two
 * shells — the same reason the screens share their state hooks.
 */
import { useRef, useState } from 'react';
import { ApiError, adminApi } from '../api';
import ImageCropModal from './ImageCropModal';

/* The three glyphs the slot draws. Copied with it rather than imported back out of the page. */
function IconTrash({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    </svg>
  );
}

function IconPhoto({ size = 26 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"></rect><circle cx="9" cy="9" r="2"></circle>
      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
    </svg>
  );
}

function IconCrop({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2v14a2 2 0 0 0 2 2h14"></path><path d="M18 22V8a2 2 0 0 0-2-2H2"></path>
    </svg>
  );
}


/**
 * Drag-and-drop image slot matching the Claude Design mockup's `image-slot` component:
 * a full-bleed preview when an image is set, a dashed/solid placeholder otherwise,
 * and (unless `bare`) a label row with an inline remove button above it.
 */
export function ImageSlot({
  token, eventSlug, imageKey, onChange, alt, aspect = '21 / 9', size, label, sub, placeholder = 'ลากไฟล์มาวาง หรือคลิกเพื่อเลือก', bare, fit = 'cover',
  enableCrop, cropAspect, maxDimension = 1600,
}: {
  token: string;
  eventSlug?: string;
  imageKey: string | null;
  onChange: (key: string | null) => void;
  alt: string;
  aspect?: string;
  size?: number;
  label?: string;
  sub?: string;
  placeholder?: string;
  bare?: boolean;
  /** 'cover' crops to fill the frame (banners); 'contain' shows the whole image (QR codes, logos). */
  fit?: 'cover' | 'contain';
  /** Opens a crop-then-convert-to-WebP dialog before uploading, instead of uploading the raw file. */
  enableCrop?: boolean;
  /** Locks the crop box to a ratio (e.g. 21/9). Omit for a freely resizable box. */
  cropAspect?: number;
  /** Cap on the exported image's longest side, in px — downscale only. */
  maxDimension?: number;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pickedFileUrl = useRef<string | null>(null);

  const upload = async (file: File) => {
    setBusy(true);
    setErr('');
    try {
      const { key } = await adminApi.uploadContentImage(token, file, eventSlug);
      onChange(key);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'อัปโหลดรูปไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const handleFile = (file: File) => {
    if (!enableCrop) {
      upload(file);
      return;
    }
    pickedFileUrl.current = URL.createObjectURL(file);
    setCropSrc(pickedFileUrl.current);
  };

  const closeCropper = () => {
    if (pickedFileUrl.current) {
      URL.revokeObjectURL(pickedFileUrl.current);
      pickedFileUrl.current = null;
    }
    setCropSrc(null);
  };

  return (
    <div style={size ? { width: size, flex: 'none' } : undefined}>
      {!bare && label && (
        <div className="admin-image-label-row">
          <div className="admin-image-label">{label}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {enableCrop && imageKey && (
              <button
                type="button"
                className="admin-review-icon-btn"
                style={{ width: 30, height: 30 }}
                title="แก้ไขการครอบตัด"
                onClick={(e) => {
                  e.stopPropagation();
                  setCropSrc(adminApi.contentImageUrl(imageKey, eventSlug));
                }}
              >
                <IconCrop />
              </button>
            )}
            {imageKey && (
              <button
                type="button"
                className="admin-review-icon-btn reject"
                style={{ width: 30, height: 30 }}
                title="ลบรูป"
                onClick={() => onChange(null)}
              >
                <IconTrash />
              </button>
            )}
          </div>
        </div>
      )}
      <div
        className={`admin-image-slot ${dragOver ? 'drag' : ''}`}
        style={size ? { width: size, height: size } : { aspectRatio: aspect }}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file) handleFile(file);
        }}
      >
        {imageKey ? (
          <img src={adminApi.contentImageUrl(imageKey, eventSlug)} alt={alt} style={{ objectFit: fit }} />
        ) : (
          <div className="placeholder">
            <IconPhoto size={size && size < 80 ? 18 : 26} />
            {!size || size >= 80 ? <span>{busy ? 'กำลังอัปโหลด…' : placeholder}</span> : null}
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
          e.target.value = '';
        }}
      />
      {sub && <div style={{ fontSize: 11.5, color: 'var(--dim)', marginTop: 8, lineHeight: 1.5 }}>{sub}</div>}
      {err && <div className="form-error">{err}</div>}
      {cropSrc && (
        <ImageCropModal
          src={cropSrc}
          aspect={cropAspect}
          maxDimension={maxDimension}
          title={`ครอบตัดรูป${label ? ` — ${label}` : ''}`}
          onCancel={closeCropper}
          onConfirm={(blob) => {
            closeCropper();
            upload(new File([blob], `${alt || 'image'}.webp`, { type: 'image/webp' }));
          }}
        />
      )}
    </div>
  );
}
